<template>
  <div class="page">
    <p>
      It has been well acknowledged that environmental factors make enormous contributions to human health. As a new strategy for
      evidencing environmental risk factors to various diseases, exposome has become the focal point of next-generation environmental health studies.
      It encompasses all environmental exposures from conception onwards and offers a "perfect" solution to use all environmental factors for human health risk assessment.
      Exposomic studies typically leverage multiple-dimensional data, including several to thousands of external exposure factors such as physical, chemical, biological,
      or even social activities. Despite its appeal, the exposome concept has been challenged in many respects such as study design, analytical capability,
      and measurement cost of hundreds to thousands of exposures. Compared to the booming genomics, proteomics, and metabolomics, the characterization and analysis of
      exposome are still at its infancy. A standardized workflow for exposomic analysis is in high demand.
    </p>
    <p>
      The current human exposome primarily emphasizes the total environmental exposure during an entire life. The characteristics
      of "lifelong" and "all environmental factors" make it very challenging to bring the exposomic study into real-life applications. Herein, we mainly discuss
      the typical application scenarios of exposomics and how to conduct an exposomic study to establish relationships between the exposome and human health.
      To increase the feasibility and efficiency, we propose that (1) an exposomic study can start with health events during critical-window periods;
      (2) both data- and hypothesis-driven exposomics should be combined to prioritize the risk of environmental factors; and (3) reliable statistical
      analysis of high-dimensional data of external and internal exposure factors are urgently needed. With standardization of the exposomic study,
      it will be critical to build a "wonderland" for human health (The Innovation, 2021, 2: 100172). However, an integrated exposome platform that meets these requirements is
      still lacking. Although exposomic studies are still in their infancy with many unknowns, they are highly expected to start booming in the historical context of human health.
      However. Supported by advanced monitoring technology of exposomics to overcome various barriers, human health assessment can evolve from the conventional health risk
      assessment to its next generation (Figure 1).
    </p>
    <div class="figure">
      <div class="figure-img">
        <img :src="exFig1" class="img-exFig1" alt="figure 1">
      </div>
      <div class="figure-description">
        <span class="figure-description-title">Figure 1</span> . Critical-window exposome with prioritized exposure factors increases the feasibility of the conventional
        exposome in human health risk assessment. The critical window can be defined by the development of one specific disease. The risk importance of exposome
        factors from both exogenous and endogenous processes for the specific disease should be meta-analyzed and prioritized. To achieve this goal, appropriate
        epidemiological design, followed by exposure prioritization, biomarker databases, analytical platforms, statistical analysis, computational toxicity, experimental
        validation, and even policy evaluation, needs to be further standardized. Source from Fang et al., The Innovation, 2021, 2: 100172
      </div>
    </div>
    <p>
      Overall,  there are three major challenges that hinder its development and application:
    </p>
    <p>
      <b>First</b>, integrating and interpreting high-dimensional data—including external exposome, internal exposome,
      and disease outcomes—to establish their associations is extremely difficult. Exposomic studies employ various epidemiological designs such as cross-sectional,
      case-control, cohort, longitudinal, and panel studies, each requiring different data pre-treatment and statistical methods. Additionally, different statistical
      analyses may yield varied results, making cross-comparison of different methods valuable for obtaining robust results with high confidence. Although many statistical
      packages (e.g., Python or R programs) are available, there is still no standardization of statistical methods for exposomic studies, which is crucial for beginners in the field.
    </p>
    <p>
      <b>Second</b>, understanding the biological mechanisms linking external exposures, endogenous changes, and diseases is
      challenging. Biological understanding is crucial for scientists to exclude confounding effects and generate high-quality hypotheses for further validation. However,
      there is no existing platform that effectively integrates and interprets the complex relationships between exposures, endogenous biological changes, and diseases.
    </p>
    <p>
       <b>Third</b>, there is a big demand to efficiently screen the features from mass spectrometry with significant contribution
       to the concerned diseases. Among all the possible technologies in the exposomic profiling, non-targeted analysis (NTA) has the advantage of high coverage and high
       throughput and increasingly becomes one major promising tool in exposomoic studies. Though the data analysis remains the bottleneck. Unlike the metabolomics or proteomics
       analysis, where simple, multiple, or pairwise analyses using t-tests or ANOVA can identify dysregulated features among hundreds to thousands of features, exposomic
       analysis involves multiple variants and requires advanced statistical methods to prioritize features linked to the concerned outcomes. Additionally, different statistical
       analyses may yield distinct results, highlighting the utility of setting up multiple statistical analyses followed by cross-model comparison.
    </p>
    <p>
      We have developed an integrated platform named "ExposomeX" to bridge the "Exposure-Biology-Disease" nexus (Figure 2).
      In summary, "ExposomeX" provides the first comprehensive analytical platform for the exposomic study including both online web-based user interface (www.exposomex.cn)
      (Figure 1A) and R package (https://github.com/ExposomeX). The platform comprises six major functions including statistical analysis (E-STAT), exposome database (E-DB),
       mass spectrometry data processing (E-MS), meta-analysis (E-META), biological link (E-BIO), and data visualization (E-VIZ). These functions are interconnected as follows
       (summarized in Figure 1B): (1) E-DB supports the four functions E-MS, E-BIO, E-META, and E-STAT by providing basic information; (2) E-MS provides annotated features
       for E-STAT to build statistical models and supports bioinformatic analysis by supplying mass spectrometry information for biological explanations; (3) E-STAT identifies
       significant features, which can be confirmed by E-MS, such as metabolic and proteomic features identified through non-target analysis using high-resolution mass spectrometry.
       Relationships identified by E-STAT are further explored and confirmed by E- BIO to elucidate mechanisms or validate novel findings; (4) E-BIO offers potential biological links
       for selecting appropriate statistical models in E-STAT and E-MS; (5) E-META enhances statistical interpretation by providing meta-analysis results for E-BIO and E-STAT;
       (6) E-VIZ offers visualization for exploratory analysis and results from E-META, E-BIO, E-MS, and E-STAT. To support these six functions, we developed 15 modules, including
       10 for E-STAT (StatTidy, StatDesc, StatCros, StatMO, StatPanel, StatSurv, StatMedt, StatMix, StatHEP, StatIP), EDb for E-DB, EMS for E-MS, EMeta for E-META, EBio for E-BIO,
        and EViz for E-VIZ.
    </p>
    <p>
       Briefly, a total of 23 supporting databases were curated, encompassing exposome and diseases,
       along with their related biological pathways and molecules. The platform includes data on up to 119,247,057 unique exposures, 17,186 diseases, 153,780 proteins, 19,122
       metabolites, 572,278 annotated pathways, and 43,324 annotated gene ontology IDs, along with their established links. Using ExposomeX, we can effectively analyze
       multidimensional exposomic data and investigate the "Exposure-Biology-Disease" nexus by exploring association strengths, understanding statistical and biological
       mechanisms, enhancing prediction performance, and automatically conducting meta-analyses based on high-quality literature databases and publications. The performance of
       ExposomeX has been well validated by re-analyzing three typical multi-omic datasets. The results revealed that ExposomeX can efficiently help discover new associations,
       as well as the relevant in-depth biological network analysis via protein, protein-protein interaction, gene, gene ontology, and pathway. In sum, we have proposed a comprehensive
       new framework for comprehensive exposomic analysis, accessible both through R <a href="https://github.com/ExposomeX" target="_blank" style="color: #0563c1" >(https://github.com/ExposomeX)</a>
       and an online interactive platform <a href="http://www.exposomex.cn/" target="_blank" style="color: #0563c1" >(http://www.exposomex.cn/)</a>.
    </p>
    <div class="figure">
      <div class="figure-img">
        <img :src="exFig2" class="img-exFig2" alt="figure 2">
      </div>
      <div class="figure-description">
        <span class="figure-description-title">Figure 2</span> . The ExposomeX platform design. (A) Main page of the web-based user interface; (B) Interactions between
        the six functions of the platform. The arrows indicate the supporting roles from the start function to the end function, with different
        colors distinguishing the connections of the individual functions.
      </div>
    </div>
  </div>
</template>

<script>
import exFig1 from '@/assets/mainpage/ex-fig1.png';
import exFig2 from '@/assets/mainpage/ex-fig2.png';
export default {
  data() {
    return {
      exFig1,
      exFig2,
    };
  },
  methods: {},
};
</script>

<style scoped lang="scss">
p {
  text-indent: 2em;
  margin-bottom: 1em;
  word-break: break-word;
  overflow: hidden;
  line-height: $line-height-small;
  text-align: justify;
}

.page {
  margin: 2em 12em;
  font-size: $webpage-base-font-size;
  font-family: $paper-page-font-family;
}

.figure {
  margin: 1em 0;
  text-align: center;
}

.figure-img {
  margin-bottom: 1em;
}

.img-exFig1 {
  width: 800px;
}

.img-exFig2 {
  width: 1000px;
}

.figure-description {
  font-size: $webpage-small-font-size;
  text-align: left;
  word-break: keep-all;
  word-wrap: normal;
}

.figure-description-title {
  font-weight: 600;
}
@media screen and (min-width: 601px) and (max-width: 1280px) {
  .page {
    margin: 2em 4em;
  }
  .img-exFig1, .img-exFig2 {
    width: 600px;
  }
}
@media screen and (max-width: 600px) {
  .page {
    margin: 1em 2em;
  }
  .img-exFig1, .img-exFig2 {
    width: 280px;
  }
}

</style>
